import { useMemo, useState } from 'react'

import { useInfiniteBusinessClientsQuery } from '@/hooks/business/useBusinessClientsQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useDebounce } from '@/hooks/useDebounce'
import { ClientInterface } from '@/interfaces/ServerInterfaces'

import { ClientData } from '../types'

export function useClientSearch() {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  // Use infinite scroll for better performance with large client datasets
  const {
    businessClients,
    isLoading: isLoadingBusinessClients,
    isLoadingMore,
    fetchNextPage,
    hasNextPage,
    totalItems,
    loadedCount,
  } = useInfiniteBusinessClientsQuery({
    businessId: businessData?.id,
    params: {
      orderBy: 'createdAt',
      order: 'desc',
      search: debouncedSearchQuery,
    },
    options: {
      enabled: !!businessData?.id && !isLoadingBusinessData,
    },
  })

  // Filter and search clients (now working with flattened infinite data)
  const filteredClients = useMemo(() => {
    const allClients = businessClients || []

    // Early return if no clients to prevent errors
    if (!Array.isArray(allClients) || allClients.length === 0) {
      return []
    }

    // If no search query, return all loaded clients
    if (!debouncedSearchQuery.trim()) {
      return allClients
    }

    // Filter clients based on search criteria
    const searchLower = debouncedSearchQuery.toLowerCase()
    return allClients.filter((client: ClientInterface) => {
      // Add null checks for client properties
      if (!client) return false

      const fullName =
        client.name ||
        `${client.firstName || ''} ${client.lastName || ''}`.trim()
      return (
        fullName.toLowerCase().includes(searchLower) ||
        (client.email || '').toLowerCase().includes(searchLower) ||
        client.ci?.toString().toLowerCase().includes(searchLower)
      )
    })
  }, [businessClients, debouncedSearchQuery])

  // Get clients with selected client always at top
  const getClientsWithSelected = (selectedClient?: ClientData) => {
    if (!selectedClient || !Array.isArray(filteredClients))
      return filteredClients || []

    const otherClients = filteredClients.filter(
      (c: ClientInterface) => c?.id !== selectedClient.id
    )
    return [selectedClient, ...otherClients]
  }

  // Create scroll handler for infinite loading in the dropdown
  const handleClientsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Don't handle scroll if we don't have proper infinite query state
    if (!hasNextPage || isLoadingMore || isLoadingBusinessClients) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

    // Check if scrolled near the bottom and there's more data to load
    if (scrollHeight - scrollTop <= clientHeight + 10) {
      fetchNextPage()
    }
  }

  return {
    searchQuery,
    setSearchQuery,
    filteredClients,
    getClientsWithSelected,
    isLoadingBusinessClients,
    isLoadingMore,
    hasNextPage,
    handleClientsScroll,
    totalItems,
    loadedCount,
  }
}
